import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Leaf,
  MapPin,
  MessageSquareOff,
  PhoneCall,
  Presentation,
  ShieldCheck,
  TreePine,
  Users,
  Video,
  Wallet,
} from "lucide-react";

export const CORPORATE_CARPOOL_SEO = {
  title: "Free Corporate Carpool Program for Companies | Duri Ridesharing",
  description:
    "Launch a free corporate carpool & employee commute program with Duri. Cut fuel costs, ease parking, reduce CO₂ and power your CSR & ESG goals — guests save ~₹7,920/month, hosts gain ~₹13,200/month.",
  keywords: [
    "corporate carpool program",
    "employee carpool",
    "office commute carpool",
    "corporate ridesharing",
    "daily commute carpool",
    "carpool for companies",
    "employee transportation program",
    "CSR mobility program",
    "ESG sustainable commute",
    "reduce carbon emissions commute",
    "fuel savings for employees",
    "green mobility",
    "Duri ridesharing",
    "carpool Hyderabad",
    "shared office commute",
    "corporate sustainability initiative",
  ].join(", "),
} as const;

export const CORPORATE_COMPANIES = [
  "Microsoft",
  "Infosys",
  "TCS",
  "Wipro",
  "Accenture",
  "Tech Mahindra",
  "Google",
  "Amazon",
] as const;

export const HERO_CHIPS: { icon: LucideIcon; label: string }[] = [
  { icon: Wallet, label: "Up to ₹79,200 saved / year" },
  { icon: ShieldCheck, label: "Verified colleagues only" },
  { icon: MapPin, label: "Live GPS tracking" },
  { icon: Leaf, label: "100% Free for companies" },
  { icon: MessageSquareOff, label: "No WhatsApp Groups Needed" },
];

export const BENEFIT_ICONS = [Users, Building2, TreePine] as const;
export const BENEFIT_ACCENTS = [
  "from-emerald-500 to-teal-500",
  "from-green-600 to-emerald-500",
  "from-teal-500 to-cyan-500",
] as const;
export const HELP_ICONS = [Presentation, Video, PhoneCall] as const;

export type DualStat = { role: string; value: string; sub: string };
export type BenefitCard = {
  title: string;
  stat?: string;
  statLabel?: string;
  dualStats?: DualStat[];
  benefits: string[];
};

export type SavingRow = { detail: string; amount: string };
export type SavingPlan = {
  role: string;
  tagline: string;
  monthly: string;
  monthlyLabel: string;
  yearly: string;
  rows: SavingRow[];
};

export const corporateCarpoolCopy = {
  heroBadge: "Free for every organisation",
  heroTitle: "Free Corporate Carpool Program",
  heroSubtitle: "Smarter employee commutes. Zero cost to your company.",
  heroTagline:
    "Cut fuel spend, ease parking pressure, lower CO₂, and power your CSR & ESG goals — while employees save thousands every month.",
  heroCta: "Bring Duri to Your Office",
  whyEyebrow: "Why Carpool",
  whyTitle: "A Win-Win for Everyone Involved",
  employeeBenefits: {
    title: "For Employees",
    dualStats: [
      { role: "Guest", value: "₹7,920", sub: "saved / month*" },
      { role: "Host", value: "₹13,200", sub: "better off / month*" },
    ],
    benefits: [
      "Share verified rides with colleagues only",
      "Live GPS tracking on every trip",
      "No chaotic WhatsApp groups",
      "Flexible host or guest roles",
    ],
  } satisfies BenefitCard,
  employerBenefits: {
    title: "For Employers",
    stat: "₹0",
    statLabel: "setup cost for your organisation",
    benefits: [
      "Free on-site presentation & webinar",
      "Less parking demand on campus",
      "Supports CSR & ESG reporting",
      "Happier, more affordable commuting",
    ],
  } satisfies BenefitCard,
  cityBenefits: {
    title: "For the City",
    stat: "CO₂↓",
    statLabel: "fewer single-occupancy trips",
    benefits: [
      "Reduced peak-hour congestion",
      "Lower fuel consumption citywide",
      "Cleaner air for everyone",
      "Scalable green mobility habit",
    ],
  } satisfies BenefitCard,
  hostGuest: {
    badge: "Real savings",
    title: "Host vs Guest — what you actually save",
    description:
      "Illustrative monthly maths for a typical 80 km daily office commute in a Honda City. Your numbers will vary by route, fuel price, and occupancy.",
    assumptionsTitle: "Assumptions used in this example",
    assumptions: [
      "80 km round-trip daily office commute",
      "22 working days in a month",
      "Honda City class vehicle & typical fuel cost",
      "Host shares seats with verified guests",
    ],
    host: {
      role: "Host",
      tagline: "Drive your car, share seats, offset fuel",
      monthly: "₹13,200",
      monthlyLabel: "Better off / month",
      yearly: "≈ ₹1,58,400 / year",
      rows: [
        { detail: "Typical solo fuel & running cost", amount: "Higher alone" },
        { detail: "Seat contributions from guests", amount: "Offsets cost" },
        { detail: "Net monthly advantage", amount: "₹13,200" },
      ],
    } satisfies SavingPlan,
    guest: {
      role: "Guest",
      tagline: "Ride with colleagues, skip solo fuel & parking stress",
      monthly: "₹7,920",
      monthlyLabel: "Saved / month",
      yearly: "≈ ₹95,040 / year",
      rows: [
        { detail: "Avoided solo commute spend", amount: "Significant" },
        { detail: "Affordable seat fare on Duri", amount: "Shared cost" },
        { detail: "Net monthly savings", amount: "₹7,920" },
      ],
    } satisfies SavingPlan,
    footnote:
      "*Example figures for an 80 km daily Honda City commute. Actual savings depend on distance, vehicle, occupancy, and local fuel prices.",
  },
  impact: {
    title: "Impact your organisation will feel",
    description:
      "Corporate carpool is a practical sustainability lever — measurable for ESG, meaningful for employees, and free to launch with Duri.",
    items: [
      "Lower collective fuel and cab spend",
      "Reduced parking pressure near offices",
      "Stronger colleague community on shared routes",
      "Credible CSR narrative around green mobility",
    ],
  },
  howWeHelp: {
    badge: "Onboarding",
    title: "How we help you launch",
    description:
      "We handle awareness and onboarding so HR and facilities teams don’t need to build a program from scratch.",
    steps: [
      {
        title: "On-site presentation",
        text: "We visit your office and walk leadership and employees through how corporate carpool works.",
      },
      {
        title: "Exclusive employee webinar",
        text: "A dedicated online session for your workforce covering app setup, safety, and matching.",
      },
      {
        title: "Personal assistance",
        text: "Our team follows up with calls so HR and early adopters get smooth, hands-on support.",
      },
    ],
  },
  carousel: {
    title: "Built for modern workplaces",
    description:
      "Ideal for campuses and tech corridors where thousands of colleagues travel similar routes every day.",
  },
  gratitude: {
    title: "Thank you for championing greener commutes",
    paragraphs: [
      "When companies open the door to carpool, employees save money, cities breathe easier, and sustainability stops being a slide in a deck.",
      "Duri’s corporate program is designed to be free, simple, and colleague-first — verified riders, live tracking, and no messy group chats.",
    ],
    programTitle: "What’s included at no cost",
    programItems: [
      "Program setup for your organisation",
      "On-site awareness presentation",
      "Employee webinar",
      "Ongoing onboarding assistance",
    ],
  },
  closing: {
    badge: "Our note to you",
    quote:
      "Small shared rides compound into big savings — for wallets, parking lots, and the planet.",
    paragraphs: [
      "If you’re ready to bring a free corporate carpool program to your workplace, we’d love to partner with your HR and sustainability teams.",
      "Share a few details below and we’ll reach out with next steps.",
    ],
    signOff: "With greener miles ahead,",
    team: "Team Duri",
  },
  form: {
    title: "Connect with our corporate team",
    description:
      "Tell us about your organisation. We’ll help you launch a free corporate carpool program for your employees.",
    nameLabel: "Your name",
    namePlaceholder: "Full name",
    emailLabel: "Work email",
    emailPlaceholder: "you@company.com",
    companyLabel: "Company name",
    companyPlaceholder: "Organisation",
    phoneLabel: "Phone (optional)",
    phonePlaceholder: "+91 …",
    messageLabel: "Message (optional)",
    messagePlaceholder: "Headcount, campuses, or preferred kickoff date…",
    submitButton: "Submit enquiry",
    submitting: "Submitting…",
    successMessage: "Thanks! Our team will get back to you shortly.",
    errorMessage: "Something went wrong. Please try again in a moment.",
    requiredMessage: "This field is required",
    invalidEmailMessage: "Please enter a valid email address",
  },
  talkEyebrow: "Let's Talk",
} as const;

export const CORPORATE_JSON_LD: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Corporate Carpool Program",
    name: "Duri Free Corporate Carpool Program",
    description:
      "A free employee carpool and daily commute program for organisations. Duri helps companies cut commute costs, reduce parking demand and CO₂ emissions, and advance CSR & ESG goals.",
    provider: {
      "@type": "Organization",
      name: "Duri Ridesharing",
      url: "https://duriapp.com/",
    },
    areaServed: { "@type": "Country", name: "India" },
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Companies, HR teams and sustainability leaders",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      description: "Free corporate carpool setup and onboarding for organisations.",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Is the Duri Corporate Carpool Program free for companies?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Duri sets up the corporate carpool program completely free for your organisation, including an on-site presentation and an exclusive employee webinar.",
        },
      },
      {
        "@type": "Question",
        name: "How much can employees save by carpooling with Duri?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "For a typical 80 km daily commute in a Honda City, a guest saves about ₹7,920 a month (₹95,040 a year) and a host is better off by about ₹13,200 a month (₹1,58,400 a year).",
        },
      },
      {
        "@type": "Question",
        name: "How do you onboard our employees?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We visit your office for an on-site presentation, run an exclusive online webinar for your employees, and our team calls you for personal assistance and smooth onboarding.",
        },
      },
      {
        "@type": "Question",
        name: "Which cities does Duri operate in?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Duri operates across India with strong demand in tech corridors such as Hyderabad and Bangalore for daily commute and intercity carpooling.",
        },
      },
    ],
  },
];
